/**
 * Axios API Client for Nour Al-Arabia
 *
 * Features:
 * - Base configuration with environment variables
 * - Request interceptor for auth token injection
 * - Response interceptor for error handling
 * - Automatic token refresh on 401
 * - Language header for bilingual responses
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  type ApiError,
  type ApiErrorResponse,
  type RefreshResponse,
  isApiErrorResponse,
} from '@/types/api';

// =============================================================================
// Configuration
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// =============================================================================
// Token Storage (in-memory for security)
// =============================================================================

let accessToken: string | null = null;
let isRefreshing = false;

interface RefreshSubscriber {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}

let refreshSubscribers: RefreshSubscriber[] = [];

/**
 * Set the access token (called from auth store)
 */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * Get the current access token
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Clear the access token (called on logout)
 */
export function clearAccessToken(): void {
  accessToken = null;
}

// =============================================================================
// Language getter (will be set by auth store)
// =============================================================================

let getLocale: () => string = () => 'ar';

/**
 * Set the locale getter function (called from auth store initialization)
 */
export function setLocaleGetter(getter: () => string): void {
  getLocale = getter;
}

// =============================================================================
// Logout handler (will be set by auth store)
// =============================================================================

let logoutHandler: (() => void) | null = null;

/**
 * Set the logout handler (called from auth store initialization)
 */
export function setLogoutHandler(handler: () => void): void {
  logoutHandler = handler;
}

// =============================================================================
// Query cache clearer (will be set by app root)
// =============================================================================

let queryCacheClearer: (() => void) | null = null;

/**
 * Set the React Query cache clearer (registered from main.tsx).
 *
 * The QueryClient is a module-level singleton created in main.tsx. Importing
 * it directly here would create an import cycle (main -> App -> stores -> api
 * -> main), so it is injected via this setter instead, mirroring the
 * setLogoutHandler / setLocaleGetter pattern.
 */
export function setQueryCacheClearer(clearer: () => void): void {
  queryCacheClearer = clearer;
}

/**
 * Clear all cached React Query data (called on logout / auth failure to
 * prevent cross-user data bleed on shared/school devices).
 */
export function clearQueryCache(): void {
  if (queryCacheClearer) {
    queryCacheClearer();
  }
}

// =============================================================================
// Refresh Token Queue Management
// =============================================================================

/**
 * Subscribe to token refresh completion
 */
function subscribeToTokenRefresh(
  resolve: (token: string) => void,
  reject: (error: Error) => void
): void {
  refreshSubscribers.push({ resolve, reject });
}

/**
 * Notify all subscribers with new token on success
 */
function notifyRefreshSubscribersSuccess(token: string): void {
  refreshSubscribers.forEach((subscriber) => subscriber.resolve(token));
  refreshSubscribers = [];
}

/**
 * Notify all subscribers with error on failure
 */
function notifyRefreshSubscribersError(error: Error): void {
  refreshSubscribers.forEach((subscriber) => subscriber.reject(error));
  refreshSubscribers = [];
}

// =============================================================================
// Axios Instance
// =============================================================================

/**
 * Create the base axios instance
 */
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds - increased for slow operations like PDF processing
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for refresh token
});

// =============================================================================
// Request Interceptor
// =============================================================================

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Attach auth token if available
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Add language header for bilingual error messages
    config.headers['Accept-Language'] = getLocale();

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// =============================================================================
// Response Interceptor
// =============================================================================

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh endpoint itself
      if (originalRequest.url?.includes('/auth/refresh')) {
        handleAuthFailure();
        return Promise.reject(createApiError(error));
      }

      if (isRefreshing) {
        // Wait for ongoing refresh to complete
        return new Promise((resolve, reject) => {
          subscribeToTokenRefresh(
            (newToken: string) => {
              // Mark as retried so a queued request that 401s again after the
              // refresh does not trigger a second refresh cycle (matching the
              // primary path below).
              originalRequest._retry = true;
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            },
            (error: Error) => {
              reject(error);
            }
          );
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt token refresh
        const response = await axios.post<RefreshResponse>(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = response.data.accessToken;
        setAccessToken(newToken);
        notifyRefreshSubscribersSuccess(newToken);
        isRefreshing = false;

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        // Notify all waiting subscribers that refresh failed
        notifyRefreshSubscribersError(
          new Error('Token refresh failed')
        );
        handleAuthFailure();
        return Promise.reject(createApiError(error));
      }
    }

    return Promise.reject(createApiError(error));
  }
);

// =============================================================================
// Error Handling
// =============================================================================

/**
 * Create a standardized API error from axios error
 */
function createApiError(error: AxiosError<ApiErrorResponse>): ApiError {
  const response = error.response;

  // Network or timeout error
  if (!response) {
    const apiError = new Error(
      error.message || 'Network error'
    ) as ApiError;
    apiError.status = 0;
    apiError.code = 'NETWORK_ERROR';
    apiError.originalError = error;
    return apiError;
  }

  // Server returned error response
  const data = response.data as Record<string, unknown> | null;

  // Extract message from response data, with fallbacks
  const message =
    (typeof data?.message === 'string' ? data.message : null) ||
    error.message ||
    'Unknown error';

  // Extract error code from response body if present (e.g., "SCHOOL_SUSPENDED", "ENTITY_HAS_CHILDREN")
  // Otherwise fall back to deriving from HTTP status code
  const code =
    (typeof data?.code === 'string' ? data.code : null) ||
    getErrorCode(response.status);

  const apiError = new Error(message) as ApiError;
  apiError.status = response.status;
  apiError.code = code;
  apiError.details = isApiErrorResponse(data) ? data.details : undefined;
  apiError.originalError = error;

  // Extract ENTITY_HAS_CHILDREN specific fields for deletion prevention errors
  if (code === 'ENTITY_HAS_CHILDREN' && data) {
    if (typeof data.entityType === 'string') {
      apiError.entityType = data.entityType as 'LEVEL' | 'TIER' | 'SCHOOL';
    }
    if (typeof data.childType === 'string') {
      apiError.childType = data.childType as 'BOOK' | 'USER';
    }
    if (typeof data.childCount === 'number') {
      apiError.childCount = data.childCount;
    }
  }

  return apiError;
}

/**
 * Get error code string from HTTP status
 */
function getErrorCode(status: number): string {
  const codes: Record<number, string> = {
    400: 'BAD_REQUEST',
    401: 'UNAUTHORIZED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    409: 'CONFLICT',
    422: 'VALIDATION_ERROR',
    429: 'TOO_MANY_REQUESTS',
    500: 'SERVER_ERROR',
    502: 'BAD_GATEWAY',
    503: 'SERVICE_UNAVAILABLE',
  };
  return codes[status] || 'UNKNOWN_ERROR';
}

/**
 * Handle authentication failure (logout and redirect)
 */
function handleAuthFailure(): void {
  clearAccessToken();
  // Clear cached data so the next user on a shared device cannot see it.
  clearQueryCache();
  if (logoutHandler) {
    logoutHandler();
  }
}

// =============================================================================
// Convenience Methods
// =============================================================================

/**
 * Type-safe GET request
 */
export async function get<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.get<T>(url, config);
  return response.data;
}

/**
 * Type-safe POST request
 */
export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.post<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe PUT request
 */
export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.put<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe PATCH request
 */
export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.patch<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe DELETE request
 */
export async function del<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.delete<T>(url, config);
  return response.data;
}

// Export the axios instance for direct use if needed
export default api;
